import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_ROLES = ["School leader", "Teacher", "Parent", "Investor", "Other"];

export default function ContactForm({
  idPrefix = "c",
  theme = "dark",
  roles = DEFAULT_ROLES,
  roleLabel = "Role",
  rolePlaceholder = "Select role",
  showRole = true,
  showPhone = true,
  showCity = true,
  showSchool = true,
  schoolLabel = "School / Organisation",
  schoolPlaceholder = "(optional)",
  source = "contact-form",
  submitLabel = "Send",
  successTitle = "Thank you. We'll be in touch soon.",
  successBody = "A member of the LifeIQ team will read your note personally and reply within two working days.",
}) {
  const [form, setForm] = useState({ name: "", role: "", email: "", phone: "", city: "", school: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/demo-requests`, {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        school: form.school || form.role || "—",
        role: form.role || undefined,
        city: form.city || undefined,
        message: form.message || undefined,
        source,
      });
      setDone(true);
      toast.success("Thank you. We'll be in touch.");
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isDark = theme === "dark";
  const labelClass = isDark
    ? "text-white/80 text-[13px] uppercase tracking-[0.14em]"
    : "text-[#5C6470] text-[12px] uppercase tracking-[0.14em] font-semibold";
  const inputClass = isDark
    ? "bg-white/5 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-[#F57F17]"
    : "bg-white border-[#D7DADD] text-[#0D1B2A] placeholder:text-[#9AA1AB] focus-visible:ring-[#F57F17]";

  if (done) {
    return (
      <div
        data-testid={`${idPrefix}-success`}
        className={isDark ? "rounded-md border border-white/15 bg-white/5 p-8 text-white" : "rounded-md border border-[#D7DADD] bg-white p-8 text-[#0D1B2A]"}
      >
        <div className="text-[12px] uppercase tracking-[0.18em] text-[#F57F17] font-semibold">Message sent</div>
        <div className={`mt-3 font-serif-d text-2xl ${isDark ? "text-white" : "text-[#0D1B2A]"}`}>{successTitle}</div>
        <p className={`mt-2 max-w-md ${isDark ? "text-white/80" : "text-[#5C6470]"}`}>{successBody}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-name`} className={labelClass}>Name</Label>
        <Input id={`${idPrefix}-name`} data-testid={`${idPrefix}-name`} value={form.name} onChange={set("name")} className={inputClass} placeholder="Your full name" />
      </div>

      {showRole && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-role`} className={labelClass}>{roleLabel}</Label>
          <Select value={form.role} onValueChange={set("role")}>
            <SelectTrigger id={`${idPrefix}-role`} data-testid={`${idPrefix}-role`} className={inputClass}>
              <SelectValue placeholder={rolePlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-email`} className={labelClass}>Email</Label>
        <Input id={`${idPrefix}-email`} data-testid={`${idPrefix}-email`} type="email" value={form.email} onChange={set("email")} className={inputClass} placeholder="name@school.in" />
      </div>

      {showPhone && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-phone`} className={labelClass}>Phone</Label>
          <Input id={`${idPrefix}-phone`} data-testid={`${idPrefix}-phone`} value={form.phone} onChange={set("phone")} className={inputClass} placeholder="+91" />
        </div>
      )}

      {showCity && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-city`} className={labelClass}>City</Label>
          <Input id={`${idPrefix}-city`} data-testid={`${idPrefix}-city`} value={form.city} onChange={set("city")} className={inputClass} placeholder="City" />
        </div>
      )}

      {showSchool && (
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-school`} className={labelClass}>{schoolLabel}</Label>
          <Input id={`${idPrefix}-school`} data-testid={`${idPrefix}-school`} value={form.school} onChange={set("school")} className={inputClass} placeholder={schoolPlaceholder} />
        </div>
      )}

      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor={`${idPrefix}-msg`} className={labelClass}>Message</Label>
        <Textarea id={`${idPrefix}-msg`} data-testid={`${idPrefix}-message`} rows={4} value={form.message} onChange={set("message")} className={inputClass} placeholder="Tell us a little about your context." />
      </div>

      <div className="sm:col-span-2 flex items-center justify-end pt-2">
        <button
          data-testid={`${idPrefix}-submit`}
          disabled={submitting}
          className="inline-flex items-center gap-2 bg-[#F57F17] text-white px-7 py-3 text-[13px] font-semibold tracking-wide uppercase hover:brightness-95 transition disabled:opacity-70 rounded-sm"
        >
          {submitting && <Loader2 size={14} className="animate-spin" />} {submitLabel}
        </button>
      </div>
    </form>
  );
}
